"use client"

import { useState } from "react"
import Image from "next/image"
import { ChevronLeft, ChevronRight, User } from "lucide-react"

const testimonials = [
  {
    id: 1,
    logo: "/testimonials/coke.png",
    logoAlt: "Coca-Cola",
    highlight: "\"THE RESPONSIVENESS ON VERY SHORT NOTICE WAS INCREDIBLE.\"",
    text: "Champions – you were amazing. And, the responsiveness on very short notice was incredible. Having worked with many agencies over the years, I can say from experience, Champions are a step above.",
    author: "Keith Sanders",
    role: "Coca Cola",
    image: "/testimonials/testomonial.png",
  },
  {
    id: 2,
    logo: null,
    logoAlt: "Santander",
    logoText: "Santander",
    highlight: "\"QUICKLY AND EFFICIENTLY ORGANISED.\"",
    text: "It was a pleasure working with you and I look forward to working with you in the future. Thank you also to both yourself and Jack at Champions. I am really grateful for how quickly and efficiently everything was organised given our short timescales. Much appreciated!",
    author: "Caroline & Poppy",
    role: "Santander - Business Manager, Structured Finance",
  },
  {
    id: 3,
    logo: "/Company Logos/PwC_logo.svg",
    logoAlt: "PwC",
    highlight: "\"EXCEPTIONAL SERVICE AND EXPERTISE.\"",
    text: "Board Champions delivered exactly what we needed - a highly qualified candidate who understood our industry challenges. Their network and professionalism made the entire process seamless.",
    author: "Senior Partner",
    role: "PwC Advisory",
  },
  {
    id: 4,
    logo: "/Company Logos/HSBC_logo.svg",
    logoAlt: "HSBC",
    highlight: "\"OUTSTANDING TALENT NETWORK.\"",
    text: "The calibre of candidates presented by Board Champions was exceptional. They truly understand the requirements for board-level appointments and delivered beyond our expectations.",
    author: "HR Director",
    role: "HSBC UK",
  },
]

// Group testimonials in pairs for the two-card layout
const testimonialPairs = []
for (let i = 0; i < testimonials.length; i += 2) {
  testimonialPairs.push(testimonials.slice(i, Math.min(i + 2, testimonials.length)))
}

export default function HomepageTestimonials() {
  const [currentSlide, setCurrentSlide] = useState(0)

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev - 1 + testimonialPairs.length) % testimonialPairs.length)
  }

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % testimonialPairs.length)
  }

  return (
    <section className="py-16 md:py-20 bg-[#ececec]">
      <div className="mx-auto max-w-7xl px-6">
        <div className="relative">
          {/* Navigation Arrow - Left */}
          <button
            onClick={goToPrevious}
            className="absolute -left-2 md:-left-4 top-1/2 transform -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full border border-neutral-300 bg-white flex items-center justify-center text-neutral-400 hover:text-[#7294c7] hover:border-[#7294c7] transition-colors duration-300 shadow-sm"
            aria-label="Previous testimonials"
          >
            <ChevronLeft className="h-5 w-5 md:h-6 md:w-6" strokeWidth={1.5} />
          </button>

          {/* Testimonial Cards */}
          <div className="overflow-hidden mx-8 md:mx-16">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {testimonialPairs.map((pair, pairIndex) => (
                <div key={pairIndex} className="w-full flex-shrink-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {pair.map((testimonial) => (
                      <div
                        key={testimonial.id}
                        className="bg-white rounded-2xl p-6 md:p-8 shadow-sm hover:shadow-md transition-shadow duration-300"
                      >
                        {/* Logo or Image */}
                        <div className="h-16 mb-6 flex items-start">
                          {testimonial.image ? (
                            <Image
                              src={testimonial.image}
                              alt={testimonial.logoAlt}
                              width={120}
                              height={80}
                              className="h-16 w-auto object-contain rounded-lg"
                            />
                          ) : testimonial.logo ? (
                            <Image
                              src={testimonial.logo}
                              alt={testimonial.logoAlt}
                              width={140}
                              height={50}
                              className="h-10 w-auto object-contain"
                            />
                          ) : testimonial.logoText ? (
                            <span className="text-2xl font-bold text-red-600">{testimonial.logoText}</span>
                          ) : null}
                        </div>

                        {/* Highlight Quote */}
                        <h3 className="text-[#7294c7] font-bold text-lg md:text-xl uppercase tracking-wide mb-4 leading-tight">
                          {testimonial.highlight}
                        </h3>

                        {/* Full Text */}
                        <p className="text-neutral-700 text-[15px] md:text-base leading-relaxed mb-6">
                          {testimonial.text}
                        </p>

                        {/* Divider */}
                        <div className="w-full h-px bg-gradient-to-r from-[#7294c7] via-[#7294c7]/50 to-transparent mb-6" />

                        {/* Author */}
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center flex-shrink-0">
                            <User className="w-5 h-5 text-neutral-400" />
                          </div>
                          <div>
                            <p className="font-semibold text-neutral-800 text-sm md:text-base">
                              {testimonial.author}
                            </p>
                            <p className="text-neutral-500 text-xs md:text-sm">
                              {testimonial.role}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrow - Right */}
          <button
            onClick={goToNext}
            className="absolute -right-2 md:-right-4 top-1/2 transform -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full border border-neutral-300 bg-white flex items-center justify-center text-neutral-400 hover:text-[#7294c7] hover:border-[#7294c7] transition-colors duration-300 shadow-sm"
            aria-label="Next testimonials"
          >
            <ChevronRight className="h-5 w-5 md:h-6 md:w-6" strokeWidth={1.5} />
          </button>
        </div>

        {/* Carousel Indicator */}
        <div className="flex justify-center mt-8">
          <div className="flex gap-2">
            {testimonialPairs.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  index === currentSlide
                    ? "w-8 bg-[#7294c7]"
                    : "w-1.5 bg-neutral-300 hover:bg-neutral-400"
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
